/**
 * Serviço WebSocket para Atualizações de Status em Tempo Real
 * 
 * CONTEXTO DE NEGÓCIO:
 * Este serviço gerencia a conexão WebSocket com o backend para receber
 * atualizações de status de análises em tempo real. Substitui o mecanismo
 * de polling, oferecendo notificações instantâneas quando o status de uma
 * análise muda no backend.
 * 
 * OBJETIVO (TAREFA-094):
 * - Estabelecer conexão WebSocket com o endpoint `/ws/analise/status`
 * - Gerenciar o ciclo de vida da conexão (abrir, fechar, reconectar)
 * - Fornecer interface de subscrição para componentes receberem atualizações
 * - Tratar erros e desconexões de forma robusta
 * 
 * DECISÕES DE ARQUITETURA:
 * - Singleton: apenas uma instância do serviço para toda a aplicação
 * - Reconexão automática em caso de falha
 * - Sistema de callbacks para notificar componentes inscritos
 * - Tipagem forte para garantir consistência das mensagens
 * 
 * INTEGRAÇÃO COM BACKEND:
 * - Conecta ao endpoint: ws://<backend>/ws/analise/status
 * - Recebe mensagens no formato RespostaStatusAnalise (compatível com API de polling)
 * 
 * USO:
 * ```typescript
 * const servico = obterWebSocketService();
 * 
 * // Inscrever para receber atualizações
 * const unsubscribe = servico.subscribe((atualizacao) => {
 *   console.log('Atualização recebida:', atualizacao);
 * });
 * 
 * // Conectar ao WebSocket
 * servico.connect();
 * 
 * // Ao desmontar componente, desinscrever e desconectar
 * unsubscribe();
 * servico.disconnect();
 * ```
 */

/**
 * Tipo que representa uma atualização de status recebida via WebSocket
 * 
 * ESTRUTURA:
 * - Compatível com o formato retornado pela API de polling (RespostaStatusAnalise)
 * - Permite que componentes usem a mesma lógica para polling e WebSocket
 */
export interface AtualizacaoStatusAnalise {
  /** ID único da consulta/análise */
  consulta_id: string;

  /** Status atual da análise */
  status: 'INICIADA' | 'PROCESSANDO' | 'CONCLUIDA' | 'ERRO';

  /** Descrição da etapa atual do processamento */
  etapa_atual: string | number;

  /** Descrição detalhada da etapa (opcional) */
  etapa_descricao?: string;

  /** Progresso da análise (0-100) */
  progresso_percentual: number;

  /** Timestamp da última atualização (formato ISO 8601) */
  timestamp_atualizacao: string;

  /** Mensagem de erro (se status === 'ERRO') */
  mensagem_erro?: string | null;
}

/**
 * Tipo de função callback para receber atualizações
 */
export type CallbackAtualizacao = (atualizacao: AtualizacaoStatusAnalise) => void;

/**
 * Estados possíveis da conexão WebSocket
 */
type EstadoConexao = 'desconectado' | 'conectando' | 'conectado' | 'erro' | 'erro_autenticacao';

/**
 * Callback para erros de autenticação (permite que componentes renovem token)
 */
export type CallbackErroAutenticacao = () => Promise<string | null>;

/**
 * Callback para fallback de polling quando WebSocket falha múltiplas vezes
 */
export type CallbackFallbackPolling = () => void;

/**
 * Classe que gerencia a conexão WebSocket e notificações
 */
class WebSocketService {
  /** Instância do WebSocket nativo */
  private websocket: WebSocket | null = null;

  /** Estado atual da conexão */
  private estadoConexao: EstadoConexao = 'desconectado';

  /** Lista de callbacks inscritos para receber atualizações */
  private callbacks: Set<CallbackAtualizacao> = new Set();

  /** Callback para renovação de token em caso de erro de autenticação */
  private callbackRenovarToken: CallbackErroAutenticacao | null = null;

  /** Callback para fallback de polling quando WebSocket falha */
  private callbackFallbackPolling: CallbackFallbackPolling | null = null;

  /** Flag para indicar se houve erro de autenticação (código 4403 ou 403) */
  private erroAutenticacao: boolean = false;

  /** URL do endpoint WebSocket no backend */
  private urlWebSocket: string;

  /** Token JWT para autenticação */
  private tokenJwt: string | null = null;

  /** Upload ID para o WebSocket */
  private uploadId: string | null = null;

  /** Timer para reconexão automática em caso de falha */
  private timerReconexao: ReturnType<typeof setTimeout> | null = null;

  /** Intervalo de reconexão em milissegundos (5 segundos) */
  private readonly INTERVALO_RECONEXAO_MS = 5000;

  /** Número máximo de tentativas de reconexão automática */
  private readonly MAX_TENTATIVAS_RECONEXAO = 10;

  /** Contador de tentativas de reconexão */
  private tentativasReconexao: number = 0;

  /** 
   * Contador de referências: quantos componentes estão usando o serviço
   * CONTEXTO: Como este é um singleton, múltiplos componentes podem usá-lo.
   * Só devemos desconectar quando TODOS os componentes tiverem sido desmontados.
   */
  private contadorReferencias: number = 0;

  /**
   * Flag para prevenir múltiplas tentativas de conexão simultâneas
   */
  private tentandoConectar: boolean = false;

  /**
   * Flag para indicar se a conexão foi fechada manualmente (não deve reconectar)
   */
  private fechadoManualmente: boolean = false;

  constructor(tokenJwt?: string, uploadId?: string) {
    this.tokenJwt = tokenJwt || null;
    this.uploadId = uploadId || null;

    // Determinar URL do WebSocket baseado no ambiente
    // CONTEXTO: Em desenvolvimento, backend roda em localhost:8000
    //           Em produção, usar o mesmo host do frontend
    const protocolo = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = import.meta.env.VITE_API_URL
      ? import.meta.env.VITE_API_URL.replace(/^https?:\/\//, '').replace(/\/$/, '')
      : window.location.host;

    // Construir URL base: ws://localhost:8000/ws/analise/status/{upload_id}
    let urlBase = `${protocolo}//${host}/ws/analise/status`;
    if (this.uploadId) {
      urlBase += `/${this.uploadId}`;
    }

    // Adicionar token JWT como parâmetro de query se disponível
    if (this.tokenJwt) {
      const urlComToken = new URL(urlBase);
      urlComToken.searchParams.set('token', this.tokenJwt);
      urlBase = urlComToken.toString();
    }

    this.urlWebSocket = urlBase;

    console.log('[WebSocketService] Inicializado com URL:', this.urlWebSocket);
  }

  /**
   * Obtém o token JWT atual usado pelo serviço
   * 
   * @returns Token JWT ou null se não definido
   */
  public getTokenJwt(): string | null {
    return this.tokenJwt;
  }

  /**
   * Verifica se o token JWT fornecido é diferente do atual
   * 
   * @param tokenJwt - Token JWT a comparar
   * @returns true se os tokens são diferentes
   */
  public tokenJwtDiferente(tokenJwt?: string): boolean {
    return this.tokenJwt !== (tokenJwt || null);
  }

  /**
   * Verifica se o uploadId fornecido é diferente do atual
   * 
   * @param uploadId - Upload ID a comparar
   * @returns true se os uploadIds são diferentes
   */
  public uploadIdDiferente(uploadId?: string): boolean {
    return this.uploadId !== (uploadId || null);
  }

  /**
   * Inscreve um callback para receber atualizações de status
   * 
   * @param callback - Função a ser chamada quando uma atualização for recebida
   * @returns Função para cancelar a inscrição
   * 
   * EXEMPLO:
   * ```typescript
   * const unsubscribe = servico.subscribe((update) => {
   *   console.log('Nova atualização:', update);
   * });
   * 
   * // Mais tarde, para cancelar:
   * unsubscribe();
   * ```
   */
  subscribe(callback: CallbackAtualizacao): () => void {
    this.callbacks.add(callback);
    console.log('[WebSocketService] Callback inscrito. Total de callbacks:', this.callbacks.size);

    // Retorna função para cancelar inscrição
    return () => {
      this.callbacks.delete(callback);
      console.log('[WebSocketService] Callback removido. Total de callbacks:', this.callbacks.size);
    };
  }

  /**
   * Estabelece conexão com o WebSocket do backend
   * 
   * COMPORTAMENTO:
   * - Se já estiver conectado, apenas incrementa o contador de referências
   * - Se estiver conectando, aguarda e não cria nova conexão
   * - Configura handlers para eventos (open, message, error, close)
   * - Em caso de erro ou desconexão, agenda reconexão automática
   * - Incrementa contador de referências ao conectar
   */
  connect(): void {
    // Incrementar contador de referências
    this.contadorReferencias++;
    console.log('[WebSocketService] 📊 Referências ativas:', this.contadorReferencias);

    // Se já estiver conectado, não fazer nada (conexão compartilhada)
    if (this.estadoConexao === 'conectado') {
      console.log('[WebSocketService] ✅ Reutilizando conexão existente');
      return;
    }

    // Se já estiver tentando conectar, não criar outra tentativa simultânea
    if (this.estadoConexao === 'conectando' || this.tentandoConectar) {
      console.log('[WebSocketService] ⏳ Conexão já em andamento, aguardando...');
      return;
    }

    console.log('[WebSocketService] 🔌 Iniciando conexão WebSocket...');
    this.estadoConexao = 'conectando';
    this.tentandoConectar = true;
    this.fechadoManualmente = false; // Reset flag on new connection attempt

    try {
      this.websocket = new WebSocket(this.urlWebSocket);

      // Handler: Conexão estabelecida com sucesso
      this.websocket.onopen = () => {
        console.log('[WebSocketService] ✅ Conexão WebSocket estabelecida');
        this.estadoConexao = 'conectado';
        this.tentandoConectar = false;
        this.tentativasReconexao = 0; // Reset contador de tentativas

        // Limpar timer de reconexão se existir
        if (this.timerReconexao) {
          clearTimeout(this.timerReconexao);
          this.timerReconexao = null;
        }
      };

      // Handler: Mensagem recebida do servidor
      this.websocket.onmessage = (evento: MessageEvent) => {
        try {
          const rawData = evento.data as string;
          
          // CORREÇÃO: O backend pode enviar múltiplas mensagens JSON concatenadas
          // quando broadcasts acontecem muito rapidamente.
          // Ex: {"consulta_id":"..."}{"consulta_id":"..."} (dois JSONs grudados)
          const jsonMessages = this._splitConcatenatedJson(rawData);
          
          for (const jsonStr of jsonMessages) {
            try {
              // Parse da mensagem JSON
              const atualizacao = JSON.parse(jsonStr) as AtualizacaoStatusAnalise;

              console.log('[WebSocketService] 📨 Atualização recebida:', atualizacao);

              // Notificar todos os callbacks inscritos
              this.callbacks.forEach(callback => {
                try {
                  callback(atualizacao);
                } catch (erro) {
                  console.error('[WebSocketService] Erro ao executar callback:', erro);
                }
              });
            } catch (parseError) {
              console.warn('[WebSocketService] Falha ao parsear JSON individual:', jsonStr, parseError);
            }
          }
        } catch (erro) {
          console.error('[WebSocketService] Erro ao processar mensagem:', erro);
        }
      };

      // Handler: Erro na conexão
      this.websocket.onerror = (evento) => {
        console.error('[WebSocketService] ❌ Erro no WebSocket:', evento);
        this.estadoConexao = 'erro';
        this.tentandoConectar = false;
      };

      // Handler: Conexão fechada
      this.websocket.onclose = (evento) => {
        console.log('[WebSocketService] 🔌 Conexão WebSocket fechada:', {
          codigo: evento.code,
          razao: evento.reason,
          limpa: evento.wasClean,
        });

        this.tentandoConectar = false;
        this.websocket = null;

        // Verificar se foi erro de autenticação (código 4403 = token expirado/inválido)
        // Nota: Backend pode enviar 4403 para token expirado, 403 para não autorizado
        const erroAuth = evento.code === 4403 || evento.code === 403 || 
          (evento.reason && evento.reason.toLowerCase().includes('token'));
        
        if (erroAuth) {
          console.warn('[WebSocketService] ⚠️ Erro de autenticação detectado:', evento.reason);
          this.erroAutenticacao = true;
          this.estadoConexao = 'erro_autenticacao';
          
          // Tentar renovar token e reconectar
          if (this.callbackRenovarToken && this.contadorReferencias > 0) {
            console.log('[WebSocketService] 🔄 Tentando renovar token e reconectar...');
            this.tentarReconectarComNovoToken();
            return;
          }
        } else {
          this.estadoConexao = 'desconectado';
        }

        // Agendar reconexão automática apenas se:
        // 1. Não foi fechamento intencional (código 1000)
        // 2. Não foi fechado manualmente
        // 3. Ainda há componentes usando o serviço (contadorReferencias > 0)
        // 4. Não atingiu o limite de tentativas
        // 5. Não foi erro de autenticação (tratado separadamente)
        if (evento.code !== 1000 &&
          !this.fechadoManualmente &&
          !erroAuth &&
          this.contadorReferencias > 0 &&
          this.tentativasReconexao < this.MAX_TENTATIVAS_RECONEXAO) {
          this.agendarReconexao();
        } else if (this.tentativasReconexao >= this.MAX_TENTATIVAS_RECONEXAO) {
          console.error('[WebSocketService] ⚠️ Número máximo de tentativas de reconexão atingido');
          // Ativar fallback de polling se disponível
          if (this.callbackFallbackPolling) {
            console.log('[WebSocketService] 📊 Ativando fallback de polling...');
            this.callbackFallbackPolling();
          }
        } else if (this.fechadoManualmente) {
          console.log('[WebSocketService] ℹ️ Não reconectando: fechado manualmente');
        } else if (this.contadorReferencias === 0) {
          console.log('[WebSocketService] ℹ️ Não reconectando: nenhum componente usando o serviço');
        }
      };
    } catch (erro) {
      console.error('[WebSocketService] Erro ao criar WebSocket:', erro);
      this.estadoConexao = 'erro';
      this.tentandoConectar = false;

      // Só agendar reconexão se houver componentes usando
      if (this.contadorReferencias > 0) {
        this.agendarReconexao();
      }
    }
  }

  /**
   * Libera uma referência ao serviço WebSocket
   * 
   * CONTEXTO:
   * Como este é um singleton compartilhado, não desconectamos imediatamente.
   * Apenas decrementamos o contador de referências. Quando chegar a 0,
   * agendamos uma desconexão (com delay para evitar reconexões desnecessárias
   * em caso de remontagem rápida de componentes).
   * 
   * USO:
   * Componentes devem chamar este método no cleanup do useEffect, não disconnect().
   * 
   * @example
   * ```typescript
   * useEffect(() => {
   *   const servico = obterWebSocketService();
   *   servico.connect();
   *   
   *   return () => {
   *     servico.release(); // ← Usar release(), não disconnect()
   *   };
   * }, []);
   * ```
   */
  release(): void {
    // Decrementar contador de referências
    this.contadorReferencias = Math.max(0, this.contadorReferencias - 1);
    console.log('[WebSocketService] 📉 Referência liberada. Referências ativas:', this.contadorReferencias);

    // Se não há mais referências, agendar desconexão
    // NOTA: Usamos timeout para evitar desconectar e reconectar imediatamente
    // em caso de remontagem de componente (ex: React.StrictMode em dev)
    if (this.contadorReferencias === 0) {
      console.log('[WebSocketService] ⏱️ Agendando desconexão (sem mais referências)...');
      setTimeout(() => {
        // Verificar novamente após o delay (pode ter sido reconectado)
        if (this.contadorReferencias === 0) {
          this.disconnect();
        } else {
          console.log('[WebSocketService] ℹ️ Desconexão cancelada (novas referências adicionadas)');
        }
      }, 1000); // 1 segundo de delay
    }
  }

  /**
   * Fecha a conexão WebSocket de forma limpa (uso interno)
   * 
   * COMPORTAMENTO:
   * - Fecha a conexão com código 1000 (fechamento normal)
   * - Cancela qualquer tentativa de reconexão agendada
   * - Limpa referência ao WebSocket
   * - Reseta contador de referências
   * 
   * NOTA: Componentes devem usar release(), não este método diretamente.
   */
  private disconnect(): void {
    console.log('[WebSocketService] 🔌 Desconectando WebSocket...');
    this.fechadoManualmente = true; // Mark as manually closed

    // Cancelar reconexão automática se agendada
    if (this.timerReconexao) {
      clearTimeout(this.timerReconexao);
      this.timerReconexao = null;
    }

    // Fechar conexão se existir
    if (this.websocket) {
      try {
        // Código 1000 = fechamento normal (previne reconexão automática)
        this.websocket.close(1000, 'Desconexão intencional');
      } catch (erro) {
        console.error('[WebSocketService] Erro ao fechar WebSocket:', erro);
      }
      this.websocket = null;
    }

    this.estadoConexao = 'desconectado';
    this.tentandoConectar = false;
    this.contadorReferencias = 0;
    console.log('[WebSocketService] ✅ WebSocket desconectado');
  }

  /**
   * Força desconexão imediata (usado principalmente para testes ou reset)
   * 
   * ATENÇÃO: Este método ignora o contador de referências e desconecta
   * imediatamente. Use apenas em situações excepcionais.
   */
  forceDisconnect(): void {
    console.log('[WebSocketService] ⚠️ Forçando desconexão imediata...');
    this.contadorReferencias = 0;
    this.disconnect();
  }

  /**
   * Fecha a conexão WebSocket manualmente (para navegação ou desmontagem)
   * 
   * CONTEXTO: Quando o usuário navega para fora da página, devemos fechar
   * a conexão explicitamente para notificar o backend e prevenir reconexões.
   * Este método marca a conexão como fechada manualmente.
   */
  closeManually(): void {
    console.log('[WebSocketService] 🔌 Fechando conexão manualmente...');
    this.fechadoManualmente = true;
    this.disconnect();
  }

  /**
   * Agenda uma tentativa de reconexão após um intervalo
   * 
   * IMPLEMENTAÇÃO:
   * - Usa exponential backoff: cada tentativa aumenta o tempo de espera
   * - Máximo de 10 tentativas antes de desistir
   * - Intervalo base: 5 segundos
   */
  private agendarReconexao(): void {
    if (this.timerReconexao) {
      return; // Já existe uma reconexão agendada
    }

    this.tentativasReconexao++;
    const tempoEspera = this.INTERVALO_RECONEXAO_MS * this.tentativasReconexao;

    console.log(
      `[WebSocketService] 🔄 Agendando reconexão (tentativa ${this.tentativasReconexao}/${this.MAX_TENTATIVAS_RECONEXAO}) em ${tempoEspera}ms`
    );

    this.timerReconexao = setTimeout(() => {
      this.timerReconexao = null;
      this.connect();
    }, tempoEspera);
  }

  /**
   * Divide uma string com múltiplos JSONs concatenados em um array de JSONs individuais.
   * 
   * CONTEXTO: O backend pode enviar múltiplas mensagens WebSocket muito rapidamente,
   * resultando em JSONs concatenados: '{"a":1}{"b":2}'
   * 
   * @param rawData - String com um ou mais JSONs concatenados
   * @returns Array de strings JSON individuais
   */
  private _splitConcatenatedJson(rawData: string): string[] {
    if (!rawData || rawData.trim() === '') {
      return [];
    }
    
    // Estratégia: dividir por "}{" que indica fronteira entre JSONs
    // Depois adicionar de volta as chaves removidas pela divisão
    const parts = rawData.split(/\}\s*\{/);
    
    if (parts.length === 1) {
      // Apenas um JSON, retornar como está
      return [rawData.trim()];
    }
    
    // Múltiplos JSONs: reconstruir adicionando chaves de volta
    return parts.map((part, index) => {
      let json = part.trim();
      
      // Primeiro elemento: pode estar faltando "}" no final
      if (index === 0) {
        if (!json.endsWith('}')) {
          json += '}';
        }
      }
      // Último elemento: pode estar faltando "{" no início
      else if (index === parts.length - 1) {
        if (!json.startsWith('{')) {
          json = '{' + json;
        }
      }
      // Elementos do meio: faltam ambos "{" e "}"
      else {
        if (!json.startsWith('{')) {
          json = '{' + json;
        }
        if (!json.endsWith('}')) {
          json += '}';
        }
      }
      
      return json;
    });
  }

  /**
   * Tenta renovar o token e reconectar ao WebSocket
   * 
   * CONTEXTO (CORREÇÃO TAREFA-WS-AUTH):
   * Quando o WebSocket é fechado com erro de autenticação (token expirado),
   * este método usa o callback de renovação para obter um novo token e
   * criar uma nova conexão com credenciais válidas.
   */
  private async tentarReconectarComNovoToken(): Promise<void> {
    if (!this.callbackRenovarToken) {
      console.warn('[WebSocketService] ⚠️ Sem callback de renovação de token configurado');
      return;
    }

    try {
      const novoToken = await this.callbackRenovarToken();
      
      if (novoToken) {
        console.log('[WebSocketService] ✅ Token renovado com sucesso, atualizando URL e reconectando...');
        this.tokenJwt = novoToken;
        this.erroAutenticacao = false;
        
        // Reconstruir URL com novo token
        this.atualizarUrlComToken(novoToken);
        
        // Reconectar com novo token
        this.tentativasReconexao = 0; // Reset tentativas
        this.connect();
      } else {
        console.error('[WebSocketService] ❌ Falha ao renovar token, ativando fallback...');
        if (this.callbackFallbackPolling) {
          this.callbackFallbackPolling();
        }
      }
    } catch (erro) {
      console.error('[WebSocketService] ❌ Erro ao renovar token:', erro);
      if (this.callbackFallbackPolling) {
        this.callbackFallbackPolling();
      }
    }
  }

  /**
   * Atualiza a URL do WebSocket com um novo token
   */
  private atualizarUrlComToken(novoToken: string): void {
    const protocolo = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = import.meta.env.VITE_API_URL
      ? import.meta.env.VITE_API_URL.replace(/^https?:\/\//, '').replace(/\/$/, '')
      : window.location.host;

    let urlBase = `${protocolo}//${host}/ws/analise/status`;
    if (this.uploadId) {
      urlBase += `/${this.uploadId}`;
    }

    const urlComToken = new URL(urlBase);
    urlComToken.searchParams.set('token', novoToken);
    this.urlWebSocket = urlComToken.toString();
  }

  /**
   * Configura o callback para renovação de token
   * 
   * @param callback - Função assíncrona que retorna novo token ou null
   * 
   * EXEMPLO:
   * ```typescript
   * servico.setCallbackRenovarToken(async () => {
   *   const refreshToken = localStorage.getItem('refresh_token');
   *   const resposta = await renovarToken(refreshToken);
   *   return resposta.access_token;
   * });
   * ```
   */
  setCallbackRenovarToken(callback: CallbackErroAutenticacao): void {
    this.callbackRenovarToken = callback;
  }

  /**
   * Configura o callback de fallback para polling
   * 
   * @param callback - Função chamada quando WebSocket falha definitivamente
   */
  setCallbackFallbackPolling(callback: CallbackFallbackPolling): void {
    this.callbackFallbackPolling = callback;
  }

  /**
   * Verifica se houve erro de autenticação na última tentativa
   */
  isErroAutenticacao(): boolean {
    return this.erroAutenticacao;
  }

  /**
   * Retorna o estado atual da conexão
   * 
   * @returns Estado atual: 'desconectado' | 'conectando' | 'conectado' | 'erro'
   */
  getEstado(): EstadoConexao {
    return this.estadoConexao;
  }

  /**
   * Verifica se a conexão está ativa
   * 
   * @returns true se conectado, false caso contrário
   */
  isConectado(): boolean {
    return this.estadoConexao === 'conectado';
  }
}

// ==============================================================================
// INSTÂNCIA SINGLETON
// ==============================================================================

/**
 * Instância única do serviço WebSocket (singleton)
 * 
 * DECISÃO DE ARQUITETURA:
 * Usamos singleton para garantir que apenas uma conexão WebSocket seja
 * mantida por toda a aplicação, evitando múltiplas conexões desnecessárias.
 */
let instanciaWebSocketService: WebSocketService | null = null;

/**
 * Obtém a instância singleton do serviço WebSocket
 * 
 * @param tokenJwt - Token JWT opcional para autenticação
 * @returns Instância única do WebSocketService
 * 
 * NOTA: Se um token diferente for fornecido, a instância será recriada
 * para garantir que a URL WebSocket contenha o token correto.
 * 
 * EXEMPLO:
 * ```typescript
 * const servico = obterWebSocketService(tokenJwt);
 * servico.connect();
 * ```
 */
export function obterWebSocketService(tokenJwt?: string, uploadId?: string): WebSocketService {
  // Se já existe uma instância, verificar se o token ou uploadId mudou
  if (instanciaWebSocketService) {
    if (instanciaWebSocketService.tokenJwtDiferente(tokenJwt) || instanciaWebSocketService.uploadIdDiferente(uploadId)) {
      // Token ou uploadId mudou, destruir instância antiga e criar nova
      console.log('[WebSocketService] Token JWT ou Upload ID mudou, recriando instância');
      instanciaWebSocketService.forceDisconnect();
      instanciaWebSocketService = null;
    }
  }

  if (!instanciaWebSocketService) {
    instanciaWebSocketService = new WebSocketService(tokenJwt, uploadId);
    console.log('[WebSocketService] Nova instância criada (singleton)');
  }
  return instanciaWebSocketService;
}

/**
 * Destrói a instância singleton do serviço (usado principalmente em testes)
 * 
 * ATENÇÃO: Use com cuidado! Esta função é principalmente para testes ou
 * situações excepcionais onde você precisa resetar completamente o serviço.
 */
export function destruirWebSocketService(): void {
  if (instanciaWebSocketService) {
    instanciaWebSocketService.forceDisconnect();
    instanciaWebSocketService = null;
    console.log('[WebSocketService] Instância destruída');
  }
}
